
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

export interface AIConfigurationItem {
  id: string;
  key: string;
  value: any;
  description: string;
  created_at: string;
  updated_at: string;
}

// RAG-specific configuration keys
export type RAGConfigurationKey = 
  | 'similarity_threshold'
  | 'enable_query_expansion'
  | 'enable_cross_encoder'
  | 'max_knowledge_results'
  | 'progressive_threshold_enabled';

/**
 * Get RAG configuration optimized for client-side usage
 */
export const getRAGConfiguration = async (): Promise<{
  similarityThreshold: number;
  enableQueryExpansion: boolean;
  enableCrossEncoder: boolean;
  maxKnowledgeResults: number;
  progressiveThresholdEnabled: boolean;
}> => {
  try {
    const { data, error } = await supabase
      .from('ai_configuration')
      .select('key, value')
      .in('key', [
        'similarity_threshold',
        'enable_query_expansion', 
        'enable_cross_encoder',
        'max_knowledge_results',
        'progressive_threshold_enabled'
      ]);

    if (error) {
      console.warn('Error fetching RAG configuration, using defaults:', error);
      return {
        similarityThreshold: 0.45,
        enableQueryExpansion: true,
        enableCrossEncoder: false,
        maxKnowledgeResults: 8,
        progressiveThresholdEnabled: true
      };
    }

    // Convert to object with defaults
    const config = {
      similarityThreshold: 0.45,
      enableQueryExpansion: true,
      enableCrossEncoder: false,
      maxKnowledgeResults: 8,
      progressiveThresholdEnabled: true
    };

    data.forEach(item => {
      switch (item.key) {
        case 'similarity_threshold':
          config.similarityThreshold = parseFloat(item.value) || 0.45;
          break;
        case 'enable_query_expansion':
          config.enableQueryExpansion = item.value === true || item.value === 'true';
          break;
        case 'enable_cross_encoder':
          config.enableCrossEncoder = item.value === true || item.value === 'true';
          break;
        case 'max_knowledge_results':
          config.maxKnowledgeResults = parseInt(item.value) || 8;
          break;
        case 'progressive_threshold_enabled':
          config.progressiveThresholdEnabled = item.value === true || item.value === 'true';
          break;
      }
    });

    return config;
  } catch (error) {
    console.error('Error in getRAGConfiguration:', error);
    // Return safe defaults
    return {
      similarityThreshold: 0.45,
      enableQueryExpansion: true,
      enableCrossEncoder: false,
      maxKnowledgeResults: 8,
      progressiveThresholdEnabled: true
    };
  }
};

/**
 * Lấy một cấu hình cụ thể theo key với generic type
 */
export const getConfiguration = async <T>(key: string, defaultValue: T): Promise<T> => {
  try {
    const { data, error } = await supabase
      .from('ai_configuration')
      .select('value')
      .eq('key', key)
      .single();

    if (error) {
      console.warn(`Configuration ${key} not found, using default:`, defaultValue);
      return defaultValue;
    }

    return data.value as T;
  } catch (error) {
    console.error(`Error getting configuration ${key}:`, error);
    return defaultValue;
  }
};

/**
 * Lấy giá trị của nhiều cấu hình cùng một lúc
 */
export const getBulkConfigurations = async <T extends Record<string, any>>(
  defaultValues: T
): Promise<T> => {
  try {
    const keys = Object.keys(defaultValues);
    
    const { data, error } = await supabase
      .from('ai_configuration')
      .select('key, value')
      .in('key', keys);

    if (error) {
      console.warn('Error fetching bulk configurations, using defaults:', defaultValues);
      return defaultValues;
    }

    // Tạo đối tượng kết quả dựa trên giá trị mặc định
    const result = { ...defaultValues };
    
    // Cập nhật giá trị từ database
    data.forEach(item => {
      const key = item.key as keyof T;
      result[key] = item.value;
    });

    return result;
  } catch (error) {
    console.error('Error in getBulkConfigurations:', error);
    return defaultValues;
  }
};
